import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { isNil } from "lodash";
import { useEffect, useState } from "react";
import { z } from "zod";

import useCurrentUser from "../../react-query/queries/useCurrentUser";
import { setAnalyticsExperiments } from "@util/analytics";
import { FeatureFlagKey, ReduceMultiplexingVariant } from "./featureFlag";

const featureFlagSchemaMap = {
  [FeatureFlagKey.WebhooksOnboarding]: z.boolean(),
  [FeatureFlagKey.SpendingCapCTA]: z.boolean(),
  [FeatureFlagKey.LimitFreeTierActiveWebhooks]: z.boolean(),
  [FeatureFlagKey.NewDowngradeFlow]: z.boolean(),
  [FeatureFlagKey.BrevoUsageLimitEmail]: z.boolean(),
  [FeatureFlagKey.BaseGoerliDeprecation]: z.boolean(),
  [FeatureFlagKey.ReduceMultiplexing]: z.nativeEnum(ReduceMultiplexingVariant),
  [FeatureFlagKey.PersonalizedOnboarding]: z.boolean(),
  [FeatureFlagKey.zkSync]: z.boolean(),
  [FeatureFlagKey.BNBMainnet]: z.boolean(),
  [FeatureFlagKey.BNBTestnet]: z.boolean(),
  [FeatureFlagKey.NewUsageCharts]: z.boolean(),
  [FeatureFlagKey.OutageAlertBanner]: z.string(),
  [FeatureFlagKey.NavRedesign]: z.boolean(),
  [FeatureFlagKey.UsageAlerts]: z.boolean(),
};

const featureFlagSchema = z.object(featureFlagSchemaMap);

export type FeatureFlagTypes = z.infer<typeof featureFlagSchema>;

type FeatureFlagValue<T extends FeatureFlagKey> =
  | FeatureFlagTypes[T]
  | undefined;

interface IUseFeatureFlag<T extends FeatureFlagKey> {
  data: FeatureFlagValue<T>;
  loading: boolean;
}
/**
 * Hook to retrieve a feature flag from LaunchDarkly
 *
 * @param featureFlagKey The key of the feature flag to retrieve
 * @returns data: The value of the feature flag. If the Launchdarkly client has not loaded or type validation fails, it will return undefined.
 * @returns loading: A boolean indicating if the feature flag is still loading
 */
export const useLDFeatureFlag = <T extends FeatureFlagKey>(
  featureFlagKey: T,
): IUseFeatureFlag<T> => {
  const { data: user, isInitialLoading: userIsLoading } = useCurrentUser();
  const userId = user?.id;
  const ldClient = useLDClient();
  const flags = useFlags<FeatureFlagTypes>();
  const flag = flags[featureFlagKey];
  const [featureFlagValue, setFeatureFlagValue] =
    useState<FeatureFlagValue<T>>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isNil(featureFlagValue) || userIsLoading || !userId) {
      return;
    }

    setAnalyticsExperiments(userId, {
      [featureFlagKey]: featureFlagValue,
    });
  }, [featureFlagKey, featureFlagValue, user, userId, userIsLoading]);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    const parsedFlag = featureFlagSchemaMap[featureFlagKey].safeParse(flag);

    if (parsedFlag.success) {
      const featureFlagValue = parsedFlag.data as FeatureFlagTypes[T];
      setFeatureFlagValue(featureFlagValue);
    } else {
      console.warn(
        `Failed to parse feature flag '${featureFlagKey}' with errors: ${parsedFlag.error.toString()}`,
      );
    }
    setIsLoading(false);
  }, [featureFlagKey, flag, ldClient]);

  return { data: featureFlagValue, loading: isLoading };
};

export default useLDFeatureFlag;
